import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { formatEuro } from "@/lib/utils";
import { DateRange } from "./DateFilter";
import { Trophy, Star, TrendingUp, Package } from "lucide-react";
import { useWorkspace } from "@/hooks/useWorkspace";

interface ProductOfMonthProps {
  dateRange: DateRange;
}

interface ProductMonthData {
  id: string;
  name: string;
  sku: string;
  quantity: number;
  revenue: number;
  category: string | null;
}

export function ProductOfMonth({ dateRange }: ProductOfMonthProps) {
  const [product, setProduct] = useState<ProductMonthData | null>(null);
  const [loading, setLoading] = useState(true);
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id || null;

  useEffect(() => {
    if (workspaceId) fetchData();
  }, [dateRange, workspaceId]);

  const fetchData = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const { data: orderItems } = await supabase
        .from("order_items")
        .select(`
          quantity, total, product_id,
          products(id, name, sku, category),
          orders!inner(order_date, status, workspace_id)
        `)
        .eq("orders.workspace_id", workspaceId)
        .gte("orders.order_date", dateRange.from.toISOString().split("T")[0])
        .lte("orders.order_date", dateRange.to.toISOString().split("T")[0])
        .neq("orders.order_type", "purchase")
        .in("orders.status", ["completed", "delivered", "shipped"]);

      const productStats: Record<string, ProductMonthData> = {};

      orderItems?.forEach((item) => {
        const productId = item.product_id;
        const prod = item.products as any;
        if (productId && prod) {
          if (!productStats[productId]) {
            productStats[productId] = { id: productId, name: prod.name || "Producto", sku: prod.sku || "", category: prod.category, quantity: 0, revenue: 0 };
          }
          productStats[productId].quantity += Number(item.quantity) || 0;
          productStats[productId].revenue += Number(item.total) || 0;
        }
      });

      const topProduct = Object.values(productStats).sort((a, b) => b.revenue - a.revenue)[0] || null;
      setProduct(topProduct);
    } catch (error) {
      console.error("Error fetching product of the month:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="chart-container bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="mb-4">
          <h3 className="font-display text-lg font-semibold text-foreground flex items-center gap-2"><Trophy className="h-5 w-5 text-warning" /> Producto Destacado</h3>
          <p className="text-sm text-muted-foreground">El más vendido del período</p>
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="chart-container bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="mb-4">
          <h3 className="font-display text-lg font-semibold text-foreground flex items-center gap-2"><Trophy className="h-5 w-5 text-warning" /> Producto Destacado</h3>
          <p className="text-sm text-muted-foreground">El más vendido del período</p>
        </div>
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Package className="h-16 w-16 mb-4 opacity-50" /><p>No hay ventas en este período</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container bg-gradient-to-br from-primary/10 via-background to-accent/10 animate-slide-in-up overflow-hidden relative">
      <div className="absolute top-0 right-0 w-32 h-32 bg-warning/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/10 rounded-full blur-2xl" />
      <div className="relative z-10">
        <div className="mb-6">
          <h3 className="font-display text-lg font-semibold text-foreground flex items-center gap-2"><Trophy className="h-5 w-5 text-warning" /> Producto Destacado</h3>
          <p className="text-sm text-muted-foreground">El más vendido del período</p>
        </div>
        <div className="flex flex-col items-center text-center">
          <div className="relative mb-4">
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center border border-border/50">
              <Package className="h-12 w-12 text-primary" />
            </div>
            <div className="absolute -top-2 -right-2 bg-warning text-warning-foreground rounded-full p-1">
              <Star className="h-4 w-4" fill="currentColor" />
            </div>
          </div>
          <h4 className="font-display text-xl font-bold text-foreground mb-1">{product.name}</h4>
          <p className="text-sm text-muted-foreground mb-4">SKU: {product.sku}{product.category && ` • ${product.category}`}</p>
          <div className="grid grid-cols-2 gap-6 w-full max-w-xs">
            <div className="text-center p-3 rounded-lg bg-background/50">
              <p className="text-2xl font-bold text-foreground">{product.quantity}</p>
              <p className="text-xs text-muted-foreground">Unidades vendidas</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-background/50">
              <p className="text-2xl font-bold text-success">{formatEuro(product.revenue, false)}</p>
              <p className="text-xs text-muted-foreground">Ingresos generados</p>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-1 text-success text-sm"><TrendingUp className="h-4 w-4" /><span>Top performer</span></div>
        </div>
      </div>
    </div>
  );
}
